import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "./supabase";

export type MarketingAsset = {
  key: string;
  url: string | null;
  alt_text: string | null;
  kind: "image" | "video";
};

type AssetMap = Record<string, MarketingAsset>;

const MarketingAssetsContext = createContext<AssetMap>({});

/**
 * Module-level cache so every route/component in this SPA session shares one
 * fetch of `marketing_assets`, instead of re-querying on every navigation.
 */
let cachedAssets: AssetMap | null = null;
let inFlight: Promise<AssetMap> | null = null;

async function loadAssets(): Promise<AssetMap> {
  if (cachedAssets) return cachedAssets;
  if (inFlight) return inFlight;

  inFlight = supabase
    .from("marketing_assets")
    .select("key, url, alt_text, kind")
    .then(({ data, error }) => {
      if (error) {
        console.error("[MarketingAssets] failed to load overrides:", error.message);
        cachedAssets = {};
      } else {
        const map: AssetMap = {};
        for (const row of data ?? []) {
          map[row.key as string] = row as MarketingAsset;
        }
        cachedAssets = map;
      }
      inFlight = null;
      return cachedAssets;
    });

  return inFlight;
}

export function MarketingAssetsProvider({ children }: { children: ReactNode }) {
  const [assets, setAssets] = useState<AssetMap>(cachedAssets ?? {});

  useEffect(() => {
    let active = true;
    loadAssets().then((map) => {
      if (active) setAssets(map);
    });
    return () => {
      active = false;
    };
  }, []);

  return <MarketingAssetsContext.Provider value={assets}>{children}</MarketingAssetsContext.Provider>;
}

/** The full key -> asset map. Useful when looking up several keys inside a `.map()`. */
export function useMarketingAssets(): AssetMap {
  return useContext(MarketingAssetsContext);
}

/** Raw asset row (url may be null, meaning "use the built-in default"). */
export function useMarketingAsset(key: string): MarketingAsset | undefined {
  const assets = useContext(MarketingAssetsContext);
  return assets[key];
}

/** Convenience for images: returns the DB override URL, or `fallback` if none is set. */
export function useMarketingImage(key: string, fallback: string): string {
  const asset = useMarketingAsset(key);
  const url = asset?.url?.trim();
  return url ? url : fallback;
}
